/* ==========================================================================
   SPICY HOME — WALK THROUGH THE HOME
   A scroll-driven sequence of rooms. One stage, six spaces, no cards.

   ARCHITECTURE
   ------------
   Pinning is CSS `position: sticky`, NOT ScrollTrigger.pin(). pin() rewrites
   the DOM into pin-spacers, which fights Lenis and breaks on resize. Sticky
   costs nothing and needs no refresh.

   THE ANCESTOR PROBLEM (measured, not assumed)
   Palo Alto sets `overflow: hidden` on #PageContainer to stop full-bleed
   sections causing horizontal scroll. That also makes it the nearest
   scrollport — and because it never scrolls itself, every sticky descendant
   silently stops working. Measured here, stage top at 0 / 35% / 70% / 100%:

     overflow: hidden  →  -1, -1085, -2170, -3101   (never pins)
     overflow: clip    →   0,     0,     0,  -242   (pins)

   `clip` clips identically — horizontal overflow stays contained, verified —
   but does not create a scrollport, so sticky survives. It is applied as an
   inline style rather than a stylesheet rule for two reasons: theme.css is
   served cross-origin so its selector cannot be read or reliably
   out-specified, and doing it here keeps the change scoped to pages that
   actually pin something.

   Scheduling joins gsap.ticker via SpicyScroll — spicy-scroll.js states the
   rule, "One rAF loop… nothing else schedules". Measurement obeys
   spicy-motion.js: a scroll frame may only WRITE; geometry is cached.

   MOTION MODEL
   ------------
   One continuous `pos` in room-space: 0 = first room settled, n-1 = last.
   Each room derives its state from its distance to `pos`, so there is no
   queue of transitions to keep in sync and nothing to desync.

     entering (d: -1 → 0)  clip-path reveals bottom→top, scale 1.04 → 1.00
     settled  (d: 0)       full frame, no transform
     leaving  (d: 0 → 1)   drifts up, fades, scale 1.00 → 0.98

   Type moves on the same clock but a larger delta, so the words arrive
   ahead of the room. That lag is the depth.

   The `stage` attribute this file sets is what unlocks the pinned CSS. If
   this script never runs, the section stays in its static editorial rail —
   see the note at the top of sections/spicy-rooms.liquid.
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var DESKTOP = window.matchMedia('(min-width: 900px)');
  var CAN_CLIP = !!(window.CSS && CSS.supports && CSS.supports('overflow', 'clip'));

  function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }

  function init(root) {
    if (root.__roomsBooted) return;
    root.__roomsBooted = true;

    var track = root.querySelector('[data-rooms-track]');
    var rooms = Array.prototype.slice.call(root.querySelectorAll('[data-room]'));
    if (!track || rooms.length < 2) return;

    var navBtns = Array.prototype.slice.call(root.querySelectorAll('[data-room-nav]'));
    var counter = root.querySelector('[data-room-counter]');
    var n = rooms.length;

    /* Reduced motion, a phone, or a browser that cannot keep sticky alive:
       the static rail is the whole experience. No listeners, no cost. */
    if (REDUCED || !DESKTOP.matches || !CAN_CLIP) {
      root.setAttribute('data-rooms-mode', 'static');
      return;
    }
    root.setAttribute('data-rooms-mode', 'stage');

    // Without this the stage cannot pin at all. See the header note.
    var pageContainer = document.getElementById('PageContainer');
    if (pageContainer) pageContainer.style.overflow = 'clip';

    var layers = rooms.map(function (el) {
      return {
        el: el,
        media: el.querySelector('[data-room-media]'),
        copy: el.querySelector('[data-room-copy]'),
        last: null
      };
    });

    var top = 0, span = 1;

    function measure() {
      var r = track.getBoundingClientRect();
      top = r.top + window.pageYOffset;
      span = Math.max(1, track.offsetHeight - window.innerHeight);
    }

    var active = -1;

    function paint(scroll) {
      var p = clamp01((scroll - top) / span);
      var pos = p * (n - 1);

      for (var i = 0; i < n; i++) {
        var L = layers[i];
        var d = pos - i;

        // Cull: only the room on screen and its neighbours can be seen.
        if (d <= -1.02 || d >= 1.02) {
          if (L.last !== 'off') { L.el.style.visibility = 'hidden'; L.last = 'off'; }
          continue;
        }
        if (L.last === 'off') L.el.style.visibility = '';
        L.last = 'on';

        var clip, scale, ty, opacity, copyY, copyO;

        if (d < 0) {
          var t = clamp01(1 + d);
          clip = ((1 - t) * 100).toFixed(2);
          scale = (1.04 - 0.04 * t).toFixed(4);
          ty = 0;
          opacity = 1;
          copyY = ((1 - t) * 42).toFixed(2);
          copyO = t < 0.45 ? 0 : clamp01((t - 0.45) / 0.55).toFixed(3);
        } else {
          clip = 0;
          scale = (1 - 0.02 * d).toFixed(4);
          ty = (-6 * d).toFixed(2);
          opacity = clamp01(1 - d * d).toFixed(3);   // eased, not linear
          copyY = (-42 * d).toFixed(2);
          copyO = clamp01(1 - d / 0.55).toFixed(3);
        }

        L.el.style.opacity = opacity;
        // Only the settled room may take a click.
        L.el.style.pointerEvents = (d > -0.5 && d < 0.5) ? 'auto' : 'none';

        if (L.media) {
          L.media.style.clipPath = 'inset(' + clip + '% 0 0 0)';
          L.media.style.transform = 'translate3d(0,' + ty + '%,0) scale(' + scale + ')';
        }
        if (L.copy) {
          L.copy.style.transform = 'translate3d(0,' + copyY + 'px,0)';
          L.copy.style.opacity = copyO;
        }
      }

      var idx = Math.round(pos);
      if (idx !== active) {
        active = idx;
        for (var j = 0; j < navBtns.length; j++) {
          var on = j === idx;
          navBtns[j].classList.toggle('is-active', on);
          navBtns[j].setAttribute('aria-current', on ? 'true' : 'false');
        }
        if (counter) counter.textContent = ('0' + (idx + 1)).slice(-2);
      }

      if (navBtns[idx]) {
        navBtns[idx].style.setProperty('--rule', Math.abs(pos - idx).toFixed(3));
      }
    }

    /* A real control, not a decorative index: keyboard-reachable, and it
       scrolls rather than teleports. */
    navBtns.forEach(function (btn, i) {
      btn.addEventListener('click', function () {
        var target = top + (i / (n - 1)) * span;
        if (window.SpicyScroll) window.SpicyScroll.scrollTo(target, { offset: 0 });
        else window.scrollTo({ top: target, behavior: 'smooth' });
      });
    });

    /* Decorative and additive: it says nothing the DOM does not already say,
       so it is aria-hidden and pointer-events:none. Real pointers only. */
    var cursor = root.querySelector('[data-room-cursor]');
    var stageEl = root.querySelector('[data-rooms-stage]');
    if (cursor && stageEl && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      var cx = 0, cy = 0, tx = 0, ty2 = 0, on = false;

      stageEl.addEventListener('pointermove', function (e) { tx = e.clientX; ty2 = e.clientY; });
      stageEl.addEventListener('pointerenter', function () { on = true; cursor.classList.add('is-on'); });
      stageEl.addEventListener('pointerleave', function () { on = false; cursor.classList.remove('is-on'); });

      if (window.gsap) {
        window.gsap.ticker.add(function () {
          if (!on && Math.abs(tx - cx) < 0.1 && Math.abs(ty2 - cy) < 0.1) return;
          cx += (tx - cx) * 0.16;
          cy += (ty2 - cy) * 0.16;
          cursor.style.transform = 'translate3d(' + cx.toFixed(1) + 'px,' + cy.toFixed(1) + 'px,0) translate(-50%,-50%)';
        });
      }
    }

    measure();

    if (window.SpicyScroll) {
      window.SpicyScroll.onScroll(function (m) { paint(m.scroll); });
    } else if (window.gsap) {
      window.gsap.ticker.add(function () { paint(window.pageYOffset); });
    }

    var t = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(t);
      t = window.setTimeout(function () { measure(); paint(window.pageYOffset); }, 150);
    }, { passive: true });

    // Late layout — images and webfonts land after boot and move the track.
    window.addEventListener('load', function () { measure(); paint(window.pageYOffset); }, { once: true });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { measure(); paint(window.pageYOffset); }).catch(function () {});
    }

    paint(window.pageYOffset);
  }

  function boot(scope) {
    var roots = (scope || document).querySelectorAll('[data-rooms]');
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); }, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
